<script setup lang="ts">
import { computed, ref, watch } from "vue"
import { useI18n } from "vue-i18n"

import { toast } from "@/shared/lib"
import { Button, FormAlert, Label, Select, Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from "@/shared/ui"

import { civilDate } from "../model/dayMath"
import type { MappedBookingError } from "../model/mapBookingError"
import { clampToRange } from "../model/rescheduleDays"
import { useRecordsStore } from "../model/store"
import BookingDateTimePicker from "./BookingDateTimePicker.vue"

const store = useRecordsStore()
const { t } = useI18n()

const open = computed(() => store.activeSheet === "reschedule")

// --- Стан форми ---
const selectedMasterId = ref("")
const selectedSlot = ref<string | null>(null)
const selectedDate = ref<string>("")
const announceText = ref("")

// --- Оригінальна цивільна дата booking ---
const originalCivilDate = computed(() =>
  store.editingBooking
    ? civilDate(new Date(store.editingBooking.startsAt), store.salonTz)
    : "",
)

// --- Опції ---
const masterOptions = computed(() =>
  store.workingMasters.map((m) => ({ value: m.id, label: m.name })),
)

// --- Зміни відносно оригінального запису ---
const masterChanged = computed(() =>
  !!store.editingBooking && selectedMasterId.value !== store.editingBooking.masterId,
)
const hasChanges = computed(() => masterChanged.value || selectedSlot.value !== null)

// --- Помилки ---
const INLINE_KEYS = new Set<string>([
  "journal.errors.slotTaken",
  "journal.errors.concurrentModification",
  "journal.errors.outsideWorkingHours",
  "journal.errors.notEditable",
  "journal.errors.serviceNotConfigured",
])

const generalError = computed(() => {
  const key = store.mutationErrorKey
  if (!key || !INLINE_KEYS.has(key)) return undefined
  return t(key)
})

const showReload = computed(
  () => store.mutationErrorKey === "journal.errors.concurrentModification",
)

const isTerminal = computed(
  () => store.mutationErrorKey === "journal.errors.notEditable",
)

// --- Ефекти ---

watch(open, (isOpen) => {
  if (!isOpen) return
  const b = store.editingBooking
  selectedMasterId.value = b?.masterId ?? ""
  selectedSlot.value = null
  announceText.value = ""
  selectedDate.value = b
    ? clampToRange(civilDate(new Date(b.startsAt), store.salonTz), store.salonTz)
    : civilDate(new Date(), store.salonTz)
}, { immediate: true })

watch(selectedMasterId, () => {
  selectedSlot.value = null
})

watch(selectedDate, () => {
  selectedSlot.value = null
})

// --- Дії ---

function handleOpenChange(isOpen: boolean): void {
  if (!isOpen) {
    if (store.mutationStatus === 'pending') return
    const terminal = isTerminal.value
    store.closeSheet()
    if (terminal) {
      void store.refreshDay()
    }
  }
}

async function handleSubmit(): Promise<void> {
  const booking = store.editingBooking
  if (!booking || !hasChanges.value) return

  try {
    await store.submitReschedule(
      booking.id,
      {
        newMasterId: masterChanged.value ? selectedMasterId.value : null,
        newStartsAt: selectedSlot.value,
      },
      booking.version,
    )
    announceText.value = t("journal.a11y.rescheduledAnnounce")
  } catch (err) {
    const mapped = err as MappedBookingError
    if (mapped.surface === "toast") {
      if (mapped.toastVariant === "info") {
        toast.info(t(mapped.i18nKey))
      } else {
        toast.error(t(mapped.i18nKey))
      }
      if (mapped.i18nKey === "journal.errors.notFound") {
        store.closeSheet()
        void store.refreshDay()
        return
      }
    }
    if (mapped.reloadAvailability && selectedMasterId.value && booking.serviceId) {
      selectedSlot.value = null
      void store.loadAvailability(selectedMasterId.value, booking.serviceId, selectedDate.value)
    }
  }
}

async function handleReload(): Promise<void> {
  await store.reloadForConcurrency()
  selectedMasterId.value = store.editingBooking?.masterId ?? ""
  selectedDate.value = store.editingBooking
    ? clampToRange(
        civilDate(new Date(store.editingBooking.startsAt), store.salonTz),
        store.salonTz,
      )
    : selectedDate.value
  selectedSlot.value = null
}
</script>

<template>
  <!-- Live region: поза порталом, завжди в DOM -->
  <span aria-live="polite" class="sr-only">{{ announceText }}</span>

  <Sheet :open="open" @update:open="handleOpenChange">
    <SheetContent side="bottom" :close-aria-label="t('journal.reschedule.close')">
      <SheetHeader>
        <SheetTitle>{{ t("journal.reschedule.title") }}</SheetTitle>
      </SheetHeader>

      <form
        class="flex flex-col gap-0"
        data-testid="reschedule-form"
        @submit.prevent="handleSubmit"
      >
        <div class="flex flex-col gap-4 overflow-y-auto px-4 py-4">
          <!-- Загальна помилка (inline) -->
          <FormAlert
            v-if="generalError"
            data-testid="form-error"
            variant="destructive"
            :message="generalError"
          />

          <!-- Кнопка «Оновити» для concurrent_modification -->
          <Button
            v-if="showReload"
            type="button"
            variant="outline"
            size="sm"
            data-testid="btn-reload"
            :disabled="store.mutationStatus === 'pending'"
            @click="handleReload"
          >
            {{ t("journal.errors.reloadAction") }}
          </Button>

          <!-- Майстер (необов'язкова зміна) -->
          <div class="flex flex-col gap-1.5" data-testid="field-master">
            <Label for="select-reschedule-master">{{ t("journal.reschedule.master") }}</Label>
            <Select
              id="select-reschedule-master"
              v-model="selectedMasterId"
              :options="masterOptions"
              :placeholder="t('journal.reschedule.selectMaster')"
            />
          </div>

          <!-- Дата та час -->
          <BookingDateTimePicker
            :model-value="selectedSlot"
            :master-id="selectedMasterId"
            :service-id="store.editingBooking?.serviceId ?? ''"
            :date="selectedDate"
            :tz="store.salonTz"
            :current-date="originalCivilDate"
            @update:model-value="selectedSlot = $event"
            @update:date="selectedDate = $event"
          />
        </div>

        <SheetFooter class="border-t px-4 py-3">
          <Button
            type="submit"
            class="w-full"
            data-testid="btn-submit"
            :loading="store.mutationStatus === 'pending'"
            :disabled="!hasChanges || isTerminal"
          >
            {{ t("journal.reschedule.submit") }}
          </Button>
        </SheetFooter>
      </form>
    </SheetContent>
  </Sheet>
</template>
